import { useCallback, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'
import { useSWRConfig } from 'swr'

import {
  campaignBannerRecipeTileFetcher,
  standardRecipeTileFetcher,
  type CampaignBannerRecipeItem,
  type MenuResponse,
  type StandardRecipeItem,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperienceMenuSWRKey } from './useMenuExperienceNavigation'

type MenuExperienceRecipeTileRefreshQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

type UseMenuExperienceRecipeTileRefreshArgs = {
  menuId?: string
  menuContent?: MenuResponse['content']
  query: MenuExperienceRecipeTileRefreshQuery
}

type RecipeTileRefreshTargets = {
  campaignBanner: boolean
  standard: boolean
}

type RefreshedRecipeTiles = {
  campaignBanner?: CampaignBannerRecipeItem
  standard?: StandardRecipeItem
}

type RecipeTileRequest = {
  deliveryDate: string
  menuId: string
  numPortions: number
  recipeId: RecipeUUID
}

const replaceStandardRecipeTile = (
  menu: MenuResponse,
  updatedRecipe: StandardRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) =>
    contentBlock.type === 'recipe-grid'
      ? {
          ...contentBlock,
          items: contentBlock.items.map((recipe) =>
            recipe.id === updatedRecipe.id ? updatedRecipe : recipe,
          ),
        }
      : contentBlock,
  ),
})

const replaceCampaignBannerRecipeTile = (
  menu: MenuResponse,
  updatedRecipe: CampaignBannerRecipeItem,
): MenuResponse => ({
  ...menu,
  content: menu.content.map((contentBlock) =>
    contentBlock.type === 'campaign-banner'
      ? {
          ...contentBlock,
          items: contentBlock.items.map((item) =>
            item.type === 'recipe' && item.id === updatedRecipe.id ? updatedRecipe : item,
          ),
        }
      : contentBlock,
  ),
})

const hasStandardRecipeTile = (content: MenuResponse['content'], recipeId: RecipeUUID) =>
  content.some(
    (contentBlock) =>
      contentBlock.type === 'recipe-grid' &&
      contentBlock.items.some((recipe) => recipe.id === recipeId),
  )

const hasCampaignBannerRecipeTile = (content: MenuResponse['content'], recipeId: RecipeUUID) =>
  content.some(
    (contentBlock) =>
      contentBlock.type === 'campaign-banner' &&
      contentBlock.items.some((item) => item.type === 'recipe' && item.id === recipeId),
  )

const getRecipeTileRefreshTargets = (
  content: MenuResponse['content'] | undefined,
  recipeId: RecipeUUID,
): RecipeTileRefreshTargets => ({
  standard: !content || hasStandardRecipeTile(content, recipeId),
  campaignBanner: Boolean(content && hasCampaignBannerRecipeTile(content, recipeId)),
})

const hasRecipeTileRefreshTarget = ({ campaignBanner, standard }: RecipeTileRefreshTargets) =>
  campaignBanner || standard

const fetchRecipeTiles = async (
  { campaignBanner, standard }: RecipeTileRefreshTargets,
  request: RecipeTileRequest,
  onFailure: () => void,
): Promise<RefreshedRecipeTiles> => {
  const [updatedStandardRecipe, updatedCampaignBannerRecipe] = await Promise.all([
    standard
      ? standardRecipeTileFetcher(request).catch(() => {
          onFailure()

          return undefined
        })
      : undefined,
    campaignBanner
      ? campaignBannerRecipeTileFetcher(request).catch(() => {
          onFailure()

          return undefined
        })
      : undefined,
  ])

  return {
    ...(updatedStandardRecipe && { standard: updatedStandardRecipe }),
    ...(updatedCampaignBannerRecipe && { campaignBanner: updatedCampaignBannerRecipe }),
  }
}

const hasRefreshedRecipeTiles = ({ campaignBanner, standard }: RefreshedRecipeTiles) =>
  Boolean(campaignBanner || standard)

const replaceRefreshedRecipeTiles = (
  menu: MenuResponse | undefined,
  { campaignBanner, standard }: RefreshedRecipeTiles,
) => {
  if (!menu) {
    return menu
  }

  const menuWithStandardRecipe = standard ? replaceStandardRecipeTile(menu, standard) : menu

  return campaignBanner
    ? replaceCampaignBannerRecipeTile(menuWithStandardRecipe, campaignBanner)
    : menuWithStandardRecipe
}

export const useMenuExperienceRecipeTileRefresh = ({
  menuId,
  menuContent,
  query,
}: UseMenuExperienceRecipeTileRefreshArgs) => {
  const { mutate } = useSWRConfig()
  const deliveryDate = useSelector(getBasketDate)
  const basketNumPortions = useSelector(getNumPortions)
  const latestRequestByRecipeId = useRef<Partial<Record<RecipeUUID, symbol>>>({})

  const refreshRecipeTile = useCallback(
    async (recipeId: RecipeUUID, numPortions: number) => {
      const menuSWRKey = getMenuExperienceMenuSWRKey({
        deliveryDate,
        numPortions: basketNumPortions,
        query,
      })

      if (!menuId || !menuSWRKey) {
        return
      }

      const refreshTargets = getRecipeTileRefreshTargets(menuContent, recipeId)

      if (!hasRecipeTileRefreshTarget(refreshTargets)) {
        return
      }

      const requestId = Symbol('menu-experience-recipe-tile-refresh')
      latestRequestByRecipeId.current[recipeId] = requestId

      const logRefreshFailure = () => {
        datadogRum.addAction('menu_experience_recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }

      const refreshedRecipeTiles = await fetchRecipeTiles(
        refreshTargets,
        { recipeId, menuId, numPortions, deliveryDate },
        logRefreshFailure,
      )

      if (latestRequestByRecipeId.current[recipeId] !== requestId) {
        return
      }

      if (!hasRefreshedRecipeTiles(refreshedRecipeTiles)) {
        return
      }

      await mutate(
        menuSWRKey,
        (menu: MenuResponse | undefined) => replaceRefreshedRecipeTiles(menu, refreshedRecipeTiles),
        { revalidate: false },
      )
    },
    [basketNumPortions, deliveryDate, menuContent, menuId, mutate, query],
  )

  return { refreshRecipeTile }
}
